import AccountTreeOutlined from "@mui/icons-material/AccountTreeOutlined";
import ArrowForward from "@mui/icons-material/ArrowForward";
import BallotOutlined from "@mui/icons-material/BallotOutlined";
import CheckCircle from "@mui/icons-material/CheckCircle";
import ContentPasteGoOutlined from "@mui/icons-material/ContentPasteGoOutlined";
import EditNoteOutlined from "@mui/icons-material/EditNoteOutlined";
import EventAvailableOutlined from "@mui/icons-material/EventAvailableOutlined";
import HistoryOutlined from "@mui/icons-material/HistoryOutlined";
import HubOutlined from "@mui/icons-material/HubOutlined";
import PaletteOutlined from "@mui/icons-material/PaletteOutlined";
import PollOutlined from "@mui/icons-material/PollOutlined";
import TroubleshootOutlined from "@mui/icons-material/TroubleshootOutlined";
import VerifiedUserOutlined from "@mui/icons-material/VerifiedUserOutlined";
import { Box, Button, ButtonBase, Chip, Stack, Typography, alpha } from "@mui/material";
import { useRef } from "react";
import { Logo } from "../generic/components/logo";
import { AssistantLogo } from "./assistant-logo";
import { ASSISTANTS } from "./assistants";

import type { Assistant } from "./assistants";
import type { SxProps, Theme } from "@mui/material";
import type { FC, ReactNode } from "react";

export type EntryPoint =
  | "hero"
  | "tile"
  | "footer"
  | "add_assistant"
  | "add_another"
  | "view_steps";

interface McpConnectLandingProps {
  agreedLine: string | null;
  onStart: (entryPoint: EntryPoint, assistant: Assistant) => void;
}

const channelColors = { Email: "#3844E5", Slack: "#611F69" };

const timeline: { when: string; what: string; channel: keyof typeof channelColors }[] = [
  { when: "−7 days", what: "Welcome email", channel: "Email" },
  { when: "−3 days", what: "Manager prep email", channel: "Email" },
  { when: "Day 1", what: "Slack hello from the team", channel: "Slack" },
  { when: "Day 30", what: "Check-in survey", channel: "Email" },
];

const valueProps = [
  {
    icon: <ContentPasteGoOutlined />,
    title: "No more copy and paste",
    body: "Draft where you think. Your assistant puts it straight into ChangeEngine with merge tags, audience and schedule set correctly.",
  },
  {
    icon: <HubOutlined />,
    title: "Knows your account",
    body: "Programs, segments, channels, brand tones and templates are all there — so you can say “the Austin team” and it knows who you mean.",
  },
  {
    icon: <VerifiedUserOutlined />,
    title: "You stay in charge",
    body: "It drafts and schedules; your team publishes. Nothing reaches employees until someone presses publish in ChangeEngine.",
  },
];

const pillars = [
  {
    icon: <EditNoteOutlined />,
    title: "Create comms",
    body: "Emails, Slack posts and SMS — written in your brand tone, sent to the right audience, scheduled for the right moment.",
    prompts: [
      "Draft an email and a matching Slack post about our new hybrid policy, for 9am Tuesday in each person’s time zone.",
      "Chase everyone who didn’t click the benefits link with a shorter Slack nudge three days later.",
      "Find the quietest day next week and schedule the open-enrollment reminder then.",
    ],
  },
  {
    icon: <AccountTreeOutlined />,
    title: "Build whole programs",
    body: "Describe the journey once. It creates the program and every message in it, timed around start dates, birthdays, anniversaries or survey answers.",
    prompts: [
      "Build a 90-day onboarding journey with emails, Slack check-ins and a 30-day survey.",
      "Set up birthday and work-anniversary messages for the Sales team, posted in Slack at 9am.",
      "Show me everything a new hire in London receives in their first 90 days, in order.",
    ],
  },
];

const more = [
  { icon: <BallotOutlined fontSize="small" />, label: "Build and translate surveys" },
  { icon: <PollOutlined fontSize="small" />, label: "Add polls" },
  { icon: <PaletteOutlined fontSize="small" />, label: "Make on-brand posters and banners" },
  { icon: <EventAvailableOutlined fontSize="small" />, label: "Check your send calendar" },
  {
    icon: <TroubleshootOutlined fontSize="small" />,
    label: "Explain why someone missed a message",
  },
  { icon: <HistoryOutlined fontSize="small" />, label: "Show who changed what" },
];

type FlowKind = "source" | "assistant" | "changeengine";

const flowColors: Record<FlowKind, { bg: string; fg: string }> = {
  source: { bg: "#334155", fg: "#E2E8F0" },
  assistant: { bg: "#FFD401", fg: "#111827" },
  changeengine: { bg: "#5048E5", fg: "#FFFFFF" },
};

const chains: { title: string; flow: [string, FlowKind][]; prompt: string }[] = [
  {
    title: "Policy to explainer",
    flow: [
      ["Google Drive", "source"],
      ["Your assistant", "assistant"],
      ["ChangeEngine", "changeengine"],
    ],
    prompt:
      "Read our time-off policy in Drive and turn it into a friendly explainer email for new joiners.",
  },
  {
    title: "Town hall in, recap out",
    flow: [
      ["Meeting notes", "source"],
      ["Your assistant", "assistant"],
      ["ChangeEngine", "changeengine"],
    ],
    prompt:
      "Take yesterday’s town hall transcript and draft a recap email, a three-line Slack summary and an FAQ for Thursday 10am.",
  },
  {
    title: "Handbook to FAQ",
    flow: [
      ["Google Drive", "source"],
      ["Your assistant", "assistant"],
      ["ChangeEngine", "changeengine"],
    ],
    prompt:
      "Read our updated employee handbook in Google Drive and draft a Slack FAQ post covering the five biggest changes.",
  },
  {
    title: "Calendar-aware reminders",
    flow: [
      ["Calendar", "source"],
      ["Your assistant", "assistant"],
      ["ChangeEngine", "changeengine"],
    ],
    prompt: "Find the benefits fair in my calendar and schedule reminders a week and a day before.",
  },
  {
    title: "Training that gets finished",
    flow: [
      ["Learning platform", "source"],
      ["Your assistant", "assistant"],
      ["ChangeEngine", "changeengine"],
    ],
    prompt:
      "Find everyone who hasn’t finished their compliance training in our learning platform, draft a friendly Slack reminder for them and a heads-up email to their managers.",
  },
  {
    title: "You said, we did",
    flow: [
      ["ChangeEngine survey", "changeengine"],
      ["Your assistant", "assistant"],
      ["ChangeEngine draft", "changeengine"],
    ],
    prompt:
      "Close the engagement survey, summarize what people said and draft a “You said, we did” email for leadership to review.",
  },
];

const setupOutline = [
  {
    title: "Choose and agree",
    body: "Pick Claude, ChatGPT or Microsoft Copilot. The first person to set up agrees to data sharing for your whole organization.",
  },
  {
    title: "Add to your assistant",
    body: "Copy your connection URL and follow the steps for your tool.",
  },
  {
    title: "Try it",
    body: "Paste a starter prompt and review the draft in ChangeEngine.",
  },
];

const inner: SxProps<Theme> = {
  maxWidth: 1200,
  mx: "auto",
  px: 3,
  py: { xs: 6, md: 9 },
  display: "flex",
  flexDirection: "column",
  gap: 4,
};

const darkSection: SxProps<Theme> = (theme) =>
  theme.palette.mode === "dark"
    ? {
        bgcolor: "background.paper",
        borderTop: 1,
        borderBottom: 1,
        borderColor: "divider",
        color: "text.secondary",
      }
    : { bgcolor: "grey.900", color: "grey.300" };

const Eyebrow: FC<{ children: ReactNode; color?: string }> = ({
  children,
  color = "primary.main",
}) => (
  <Typography
    variant="overline"
    sx={{ color, fontWeight: 700, letterSpacing: 0.5, lineHeight: 1.5 }}
  >
    {children}
  </Typography>
);

const SectionHeading: FC<{
  eyebrow: string;
  title: string;
  body?: string;
  id?: string;
  dark?: boolean;
}> = ({ eyebrow, title, body, id, dark }) => (
  <Stack spacing={1.25} sx={{ maxWidth: 760 }}>
    <Eyebrow color={dark ? "#FFD401" : undefined}>{eyebrow}</Eyebrow>
    <Typography variant="h4" component="h2" id={id} sx={dark ? { color: "common.white" } : null}>
      {title}
    </Typography>
    {body ? (
      <Typography variant="body1" sx={{ color: dark ? "inherit" : "text.secondary" }}>
        {body}
      </Typography>
    ) : null}
  </Stack>
);

const ExampleConversation: FC = () => (
  <Box
    component="figure"
    aria-label="Example conversation"
    sx={{
      flex: "1 1 440px",
      minWidth: 0,
      m: 0,
      bgcolor: "background.paper",
      border: 1,
      borderColor: "divider",
      borderRadius: 4,
      boxShadow: 6,
      overflow: "hidden",
    }}
  >
    <Stack
      direction="row"
      spacing={1}
      useFlexGap
      sx={{
        alignItems: "center",
        px: 2,
        py: 1.5,
        borderBottom: 1,
        borderColor: "divider",
        color: "text.secondary",
      }}
    >
      {[0, 1, 2].map((dot) => (
        <Box
          key={dot}
          sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "action.disabledBackground" }}
        />
      ))}
      <Typography variant="caption" sx={{ pl: 1 }}>
        Your AI assistant
      </Typography>
      <Chip
        icon={<Logo sx={{ width: 14, height: 14 }} />}
        label="ChangeEngine connected"
        size="small"
        sx={(theme) => ({
          ml: "auto",
          fontWeight: 600,
          color: "primary.main",
          bgcolor: alpha(theme.palette.primary.main, 0.1),
        })}
      />
    </Stack>
    <Stack spacing={2} sx={{ p: 2.5 }}>
      <Typography
        variant="body2"
        sx={{
          alignSelf: "flex-end",
          maxWidth: "88%",
          bgcolor: "action.hover",
          borderRadius: "14px 14px 4px 14px",
          px: 1.75,
          py: 1.5,
        }}
      >
        Build an onboarding program for new engineers: welcome email a week before they start, Slack
        hello on day one, manager prep 3 days before, and a check-in survey at 30 days.
      </Typography>
      <Stack
        spacing={0.75}
        sx={{
          p: 1.5,
          border: 1,
          borderColor: "divider",
          borderRadius: 2.5,
          bgcolor: "action.hover",
        }}
      >
        {[
          "Created program “Engineering onboarding”",
          "Wrote 4 drafts in your brand tone",
          "Scheduled each one from start date",
          "Built the 30-day check-in survey",
        ].map((item) => (
          <Typography
            key={item}
            variant="body2"
            sx={{ display: "flex", alignItems: "center", gap: 1, color: "success.dark" }}
          >
            <CheckCircle fontSize="small" />
            {item}
          </Typography>
        ))}
      </Stack>
      <Typography variant="body2">Done. Here’s what a new engineer will get:</Typography>
      <Box
        component="ol"
        sx={{ listStyle: "none", m: 0, p: 0, ml: "6px", borderLeft: 2, borderColor: "divider" }}
      >
        {timeline.map((entry) => (
          <Box
            key={entry.what}
            component="li"
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "center",
              py: 0.75,
              pl: 2,
              position: "relative",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                left: -7,
                width: 12,
                height: 12,
                borderRadius: "50%",
                bgcolor: "background.paper",
                border: 2,
                borderColor: "primary.main",
                boxSizing: "border-box",
              }}
            />
            <Typography
              variant="body2"
              sx={{ width: 74, flexShrink: 0, fontWeight: 600, color: "primary.main" }}
            >
              {entry.when}
            </Typography>
            <Typography variant="body2" sx={{ flex: 1 }}>
              {entry.what}
            </Typography>
            <Box
              component="span"
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: "common.white",
                bgcolor: channelColors[entry.channel],
                borderRadius: 0.5,
                px: 0.75,
                py: 0.25,
              }}
            >
              {entry.channel}
            </Box>
          </Box>
        ))}
      </Box>
      <Typography variant="body2" color="textSecondary">
        All four are drafts. Open “Engineering onboarding” in ChangeEngine to review and publish.
      </Typography>
    </Stack>
  </Box>
);

export const McpConnectLanding: FC<McpConnectLandingProps> = ({ agreedLine, onStart }) => {
  const examplesRef = useRef<HTMLElement>(null);

  return (
    <Box component="main">
      <Box
        component="section"
        sx={{ bgcolor: "action.hover", borderBottom: 1, borderColor: "divider" }}
      >
        <Box
          sx={{
            maxWidth: 1200,
            mx: "auto",
            px: 3,
            py: { xs: 6, md: 9 },
            display: "flex",
            flexWrap: "wrap",
            gap: 7,
            alignItems: "center",
          }}
        >
          <Stack spacing={3} sx={{ flex: "1 1 460px", minWidth: 0 }}>
            <Box>
              <Chip label="Beta" size="small" color="primary" />
            </Box>
            <Typography
              variant="h2"
              component="h1"
              sx={{ fontWeight: 300, fontSize: "clamp(38px, 5vw, 56px)", lineHeight: 1.12 }}
            >
              Run your employee comms from the AI assistant you already use
            </Typography>
            <Typography variant="h6" component="p" color="textSecondary" sx={{ maxWidth: 560 }}>
              Connect Claude, ChatGPT or Microsoft Copilot to ChangeEngine. Ask for an email, a
              reminder or a whole onboarding journey — and it’s done in your account, ready for you
              to review.
            </Typography>
            <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap" }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => onStart("hero", "claude")}
                data-analytics-id="mcp-connect-get-started"
              >
                Get started
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => examplesRef.current?.scrollIntoView({ behavior: "smooth" })}
                data-analytics-id="mcp-connect-see-examples"
              >
                See what it can do
              </Button>
            </Stack>
            <Stack
              direction="row"
              spacing={2}
              sx={{ alignItems: "center", flexWrap: "wrap", pt: 1 }}
            >
              <Typography variant="body2" color="textSecondary">
                Works with
              </Typography>
              {ASSISTANTS.map((assistant) => (
                <Stack key={assistant.id} direction="row" spacing={1} sx={{ alignItems: "center" }}>
                  <AssistantLogo assistant={assistant} size={24} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {assistant.name}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
          <ExampleConversation />
        </Box>
      </Box>

      <Box
        component="section"
        aria-label="Why teams use it"
        sx={{
          maxWidth: 1200,
          mx: "auto",
          px: 3,
          py: 6,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: 4,
        }}
      >
        {valueProps.map((valueProp) => (
          <Stack key={valueProp.title} spacing={1}>
            <Box sx={{ color: "primary.main", display: "inline-flex" }}>{valueProp.icon}</Box>
            <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
              {valueProp.title}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {valueProp.body}
            </Typography>
          </Stack>
        ))}
      </Box>

      <Box
        component="section"
        ref={examplesRef}
        aria-labelledby="mcp-connect-examples"
        sx={{ bgcolor: "action.hover" }}
      >
        <Box sx={inner}>
          <SectionHeading
            eyebrow="What you can ask"
            title="Two jobs it takes off your plate"
            id="mcp-connect-examples"
          />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: 2.5,
            }}
          >
            {pillars.map((pillar) => (
              <Stack
                key={pillar.title}
                component="article"
                spacing={2}
                sx={{
                  bgcolor: "background.paper",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 3,
                  p: 3,
                }}
              >
                <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                  <Box sx={{ color: "primary.main", display: "inline-flex" }}>{pillar.icon}</Box>
                  <Typography variant="h6" component="h3" sx={{ fontWeight: 700 }}>
                    {pillar.title}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="textSecondary">
                  {pillar.body}
                </Typography>
                <Stack component="ul" spacing={1} sx={{ listStyle: "none", m: 0, p: 0 }}>
                  {pillar.prompts.map((prompt) => (
                    <Typography
                      key={prompt}
                      component="li"
                      variant="body2"
                      sx={{
                        bgcolor: "action.hover",
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 2,
                        px: 1.75,
                        py: 1.5,
                      }}
                    >
                      “{prompt}”
                    </Typography>
                  ))}
                </Stack>
              </Stack>
            ))}
          </Box>
          <Stack
            direction="row"
            spacing={1.25}
            useFlexGap
            sx={{ flexWrap: "wrap", alignItems: "center" }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mr: 0.5 }}>
              It can also
            </Typography>
            {more.map((item) => (
              <Chip
                key={item.label}
                icon={item.icon}
                label={item.label}
                variant="outlined"
                sx={{ bgcolor: "background.paper" }}
              />
            ))}
          </Stack>
        </Box>
      </Box>

      <Box component="section" aria-labelledby="mcp-connect-chain" sx={darkSection}>
        <Box sx={inner}>
          <SectionHeading
            eyebrow="Go further"
            title="Chain it with your other tools"
            body="Your assistant can work across every app you’ve connected to it. One request can read a policy, a transcript or a calendar — and finish in ChangeEngine."
            id="mcp-connect-chain"
            dark
          />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
              gap: 2,
            }}
          >
            {chains.map((chain) => (
              <Stack
                key={chain.title}
                component="article"
                spacing={1.75}
                sx={{
                  bgcolor: "rgba(255,255,255,.06)",
                  border: 1,
                  borderColor: "rgba(255,255,255,.12)",
                  borderRadius: 3,
                  p: 2.5,
                }}
              >
                <Stack
                  direction="row"
                  spacing={0.75}
                  useFlexGap
                  sx={{ alignItems: "center", flexWrap: "wrap" }}
                >
                  {chain.flow.map(([label, kind], index) => (
                    <Stack key={label} direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
                      <Box
                        component="span"
                        sx={{
                          px: 1,
                          py: 0.5,
                          borderRadius: 0.5,
                          fontSize: 12,
                          fontWeight: 600,
                          bgcolor: flowColors[kind].bg,
                          color: flowColors[kind].fg,
                        }}
                      >
                        {label}
                      </Box>
                      {index < chain.flow.length - 1 ? (
                        <Box component="span" aria-hidden="true" sx={{ color: "grey.500" }}>
                          →
                        </Box>
                      ) : null}
                    </Stack>
                  ))}
                </Stack>
                <Typography
                  variant="subtitle1"
                  component="h3"
                  sx={{ fontWeight: 700, color: "common.white" }}
                >
                  {chain.title}
                </Typography>
                <Typography variant="body2">“{chain.prompt}”</Typography>
              </Stack>
            ))}
          </Box>
        </Box>
      </Box>

      <Box component="section" aria-labelledby="mcp-connect-start">
        <Box sx={inner}>
          <SectionHeading
            eyebrow="Get started"
            title="Connected in about two minutes"
            body="Pick your assistant to start a short guided setup. It gives you your connection URL and walks you through adding ChangeEngine in that tool."
            id="mcp-connect-start"
          />
          {agreedLine ? (
            <Typography
              variant="body2"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontWeight: 600,
                color: "success.dark",
              }}
            >
              <VerifiedUserOutlined fontSize="small" />
              {agreedLine}
            </Typography>
          ) : null}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: 2,
            }}
          >
            {ASSISTANTS.map((assistant) => (
              <ButtonBase
                key={assistant.id}
                onClick={() => onStart("tile", assistant.id)}
                data-analytics-id={`mcp-connect-tile-${assistant.id}`}
                sx={{
                  textAlign: "left",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 1.5,
                  p: 3,
                  minHeight: 190,
                  borderRadius: 3,
                  border: 1,
                  borderColor: "divider",
                  bgcolor: "background.paper",
                  boxShadow: 1,
                  "&:hover": { borderColor: "primary.main" },
                }}
              >
                <AssistantLogo assistant={assistant} size={40} />
                <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
                  {assistant.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {assistant.plan}
                </Typography>
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{
                    mt: "auto",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    fontWeight: 600,
                  }}
                >
                  Set up {assistant.name}
                  <ArrowForward fontSize="small" />
                </Typography>
              </ButtonBase>
            ))}
          </Box>
          <Box
            component="ol"
            aria-label="What the setup covers"
            sx={{
              listStyle: "none",
              m: 0,
              px: 3,
              py: 2.5,
              borderRadius: 3,
              bgcolor: "action.hover",
              border: 1,
              borderColor: "divider",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: 2,
            }}
          >
            {setupOutline.map((item, index) => (
              <Stack key={item.title} component="li" direction="row" spacing={1.5}>
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    fontSize: 12,
                    fontWeight: 700,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {index + 1}
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {item.body}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Box>
        </Box>
      </Box>

      <Box component="footer" sx={darkSection}>
        <Box
          sx={{
            maxWidth: 1200,
            mx: "auto",
            px: 3,
            py: 6,
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h5" component="p" sx={{ fontWeight: 600, color: "common.white" }}>
            Give your assistant something worth doing.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => onStart("footer", "claude")}
            data-analytics-id="mcp-connect-footer"
            sx={{ bgcolor: "common.white", color: "grey.900", "&:hover": { bgcolor: "grey.200" } }}
          >
            Connect your assistant
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
